/** Firestore CRUD for /users/{uid} */
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { getAcquisitionForFirestore, getAcquisitionData } from '../lib/acquisition';
import { analytics } from '../lib/logger';
import type { User, Car } from '../types';

const USERS = 'users';

export async function fetchUser(uid: string): Promise<User | null> {
  const snap = await getDoc(doc(db, USERS, uid));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    email: data.email,
    displayName: data.displayName,
    cars: (data.cars ?? []) as Car[],
    reputationScore: data.reputationScore ?? 0,
    verificationsCount: data.verificationsCount ?? 0,
    homeCityId: data.homeCityId,
    createdAt: data.createdAt ?? Date.now(),
  };
}

export async function createUserDoc(
  uid: string,
  email: string | null,
  displayName?: string | null
): Promise<User> {
  const createdAt = Date.now();
  const acquisition = getAcquisitionForFirestore();
  
  await setDoc(doc(db, USERS, uid), {
    id: uid,
    email: email ?? '',
    displayName: displayName ?? '',
    cars: [],
    reputationScore: 0,
    verificationsCount: 0,
    homeCityId: 'zurich',
    createdAt,
    updatedAt: serverTimestamp(),
    // Acquisition tracking
    ...(acquisition ?? {}),
  });
  
  // Log acquisition to analytics
  const acqData = getAcquisitionData();
  if (acqData) {
    analytics.userAcquired({
      source: acqData.utmSource || acqData.referrer || 'direct',
      medium: acqData.utmMedium || (acqData.referrer ? 'referral' : 'none'),
      campaign: acqData.utmCampaign,
      referrer: acqData.referrer,
    });
  }
  
  return {
    id: uid,
    email: email ?? '',
    displayName: displayName ?? '',
    cars: [],
    reputationScore: 0,
    verificationsCount: 0,
    homeCityId: 'zurich',
    createdAt,
  };
}

export async function setUserCars(uid: string, cars: Car[]): Promise<void> {
  await updateDoc(doc(db, USERS, uid), {
    cars,
    updatedAt: serverTimestamp(),
  });
}
